/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */

import { paddedMonth, paddedDay } from './utils';
import { DateTime } from 'luxon';

export abstract class ValueConverter<T> {
  protected value: any;

  constructor(value: any) {
    this.value = value;
  }

  protected toPlainString(): string {
    if (typeof this.value === 'number') {
      return String(this.value);
    } else if (typeof this.value === 'string') {
      return this.value;
    } else if (this.value === null || typeof this.value === 'undefined') {
      return null;
    } else {
      return '';
    }
  }

  /**
   * stabilisce se il value è un numero accettabile
   */
  protected isAcceptableNumber(value: any): boolean {
    return value !== null && !isNaN(value);
  }

  /**
   * stabilisce se il value è una stringa accettabile
   */
  protected isAcceptableString(value: any): boolean {
    return typeof value === 'string' && !!value;
  }

  /**
   * stabilisce se il value è inizilizzatore della classe Date accettabile
   */
  protected isAcceptableDate(value: any): boolean {
    return value instanceof Date && !isNaN(value.getTime());
  }

  /**
   * converte il value nel formato richiesto
   */
  abstract convertTo(format?: T): string;
}

/**
 * converte il value nel formata data richiesto
 */
export class DateConverter extends ValueConverter<'yyyymmdd' | 'millisec'> {
  protected value: any;

  constructor(value: any) {
    super(value);
  }

  /**
   * converte secondo il formato richiesto
   */
  convertTo(
    format?: 'yyyymmdd' | 'millisec',
    timezoneShift?: 'add' | 'remove'
  ): string {
    switch (format) {
      case 'yyyymmdd':
        return this.toYYYYMMDD(timezoneShift);
      case 'millisec':
        return this.toMillisec(timezoneShift);
      default:
        return this.toPlainString();
    }
  }

  /**
   * converte nel formato yyyy-mm-dd
   */
  toYYYYMMDD(timezoneShift?: 'add' | 'remove'): string {
    let dt = this.createDate();

    if (!this.isAcceptableDate(dt)) {
      return null;
    }

    if (!!timezoneShift) {
      dt = new Date(this.applyTimezoneShift(dt.getTime(), timezoneShift));
    }

    const yyyy = dt.getFullYear();
    const mm = dt.getMonth() + 1;
    const dd = dt.getDate();

    return `${yyyy}-${paddedMonth(mm)}-${paddedDay(dd)}`;
  }

  /**
   * converte in formato millisecondi
   */
  toMillisec(timezoneShift?: 'add' | 'remove'): string {
    const dt = this.createDate();

    if (!this.isAcceptableDate(dt)) {
      return null;
    }

    const yyyy = dt.getFullYear();
    const mm = dt.getMonth();
    const dd = dt.getDate();
    let utcTime = Date.UTC(yyyy, mm, dd);

    if (!!timezoneShift) {
      utcTime = this.applyTimezoneShift(utcTime, timezoneShift);
    }

    return utcTime.toString();
  }

  /**
   * crea oggetto data partendo dal value
   */
  private createDate(): Date {
    if (this.isAcceptableNumber(this.value)) {
      return new Date(Number(this.value));
    } else if (this.isAcceptableString(this.value)) {
      return new Date(this.value);
    } else {
      return null;
    }
  }

  /**
   * applica lo spostamento come richiesto
   */
  private applyTimezoneShift(time: number, shift?: 'add' | 'remove'): number {
    const shiftAmount = this.isDaylightSavingTime(time) ? 3600000 * 2 : 3600000;

    if (shift === 'add') {
      time = time + shiftAmount;
    } else if (shift === 'remove') {
      time = time - shiftAmount;
    }

    if (this.isNonExistentTime(time) && shift === 'remove') {
      time += 3600000;
    }

    return time;
  }

  private isDaylightSavingTime(timestamp: number): boolean {
    if (this.isUnhandledTime(timestamp)) {
      return true;
    }

    const dt = new Date(timestamp);

    return DateTime.fromJSDate(dt).setZone('Europe/Rome').isInDST;
  }

  private isUnhandledTime(timestamp: number): boolean {
    return -915148800000 <= timestamp && timestamp < -857257201000;
  }

  private isNonExistentTime(timestamp: number): boolean {
    return [
      -1690768800000, -1664762400000, -1635127200000, -1604282400000,
      -1571018400000, -932436000000, -719460000000, -114055200000, -82000800000,
      -50551200000, -18496800000, 12952800000, 43797600000, 75852000000,
      107906400000, 138751200000, 170805600000, 202255200000, 233100000000,
      265154400000, 296604000000
    ].includes(timestamp);
  }
}

/**
 * converte il value in una string nel formato richiesto
 */
export class StringConverter extends ValueConverter<'uppercase'> {
  protected value: any;

  constructor(value: any) {
    super(value);
  }

  /**
   * converte secondo il formato richiesto
   */
  convertTo(format?: 'uppercase'): string {
    switch (format) {
      case 'uppercase':
        return this.toUppercase();
      default:
        return this.toPlainString();
    }
  }

  /**
   * converte la stringa in uppercase
   */
  protected toUppercase(): string {
    if (this.isAcceptableString(this.value)) {
      return (this.value as string).toUpperCase();
    } else {
      return null;
    }
  }
}
